import React, { useEffect, useState } from "react";
import { Row, Col, Drawer, Modal, Button } from "antd";

import DashboardHeader from "./components/DashboardHeader";
import WelcomeCard from "./components/WelcomeCard";
import KpiCards from "./components/KpiCards";
import ShortcutBar from "./components/ShortcutBar";
import DashboardCharts from "./components/DashboardCharts";
import NotificationPanel from "./components/NotificationPanel";
import ExpiryList from "./components/ExpiryList";
import ActivityList from "./components/ActivityList";
import QuickStats from "./components/QuickStats";
import EmployeeStatusPanel from "./components/EmployeeStatusPanel";
import AttendanceOverviewPanel from "./components/AttendanceOverviewPanel";
import ClockInOutPanel from "./components/ClockInOutPanel";
import EmployeeProfilePanel from "./components/EmployeeProfilePanel";
import "./styles.css";

import { useDashboardData } from "./hooks/useDashboardData";
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../Auth/authSelectors";
import { checkEmployeeRouteAccessApi, getPayrollDetailedStatus } from "../../apiservice/hrpayroll";
import PayrollDetailsModal from "./components/PayrollDetailsModal";
import PendingApprovalsModal from "./components/PendingApprovalsModal";
import UnmarkedAttendanceModal from "./components/UnmarkedAttendanceModal";
import { useNavigate } from "react-router-dom";


export default function HRPayrollDashboard() {

  const [isAdminUser, isAdminUserSet] = useState(false);
  const [activeDrawer, setActiveDrawer] = useState(null);
  const [payrollModal, setPayrollModal] = useState(false);
  const [modalTab, setModalTab] = useState("completed");
  const [pendingModal, setPendingModal] = useState(false);
  const [unmarkedAttendanceModal, setUnmarkedAttendanceModal] = useState(false);
  const [payrollDetails, setPayrollDetails] = useState([]);
  const [payrollSummary, setPayrollSummary] = useState({});
  const [loadingPayroll, setLoadingPayroll] = useState(false);
  const [activeKpi, setActiveKpi] = useState(null);
  const [hasRouteAccess, setHasRouteAccess] = useState(false);


  const data = useDashboardData();
  const authInfo = useSelector(selectAuthInfo);
  const navigate = useNavigate();

  const handleKpiClick = async (key) => {
    setActiveKpi(key);

    if (key === "clockinout") {                              // ← was "attendanceOv"
      navigate("/hrpayroll/attendance?tab=clockinout");
      return;
    }

    if (key === "onboarding") {
      navigate("/hrpayroll/onboarding");
      return;
    }

    if (key === "renewals") {
      setActiveDrawer("renewals");
      return;
    }

    if (key === "pendingApproval") {
      setPendingModal(true);
      return;
    }

    if (key === "processedPayroll" || key === "pendingPayroll") {
      setModalTab(key === "processedPayroll" ? "completed" : "partial");
      setPayrollModal(true);
      setLoadingPayroll(true);

      const res = await getPayrollDetailedStatus();
      setLoadingPayroll(false);

      if (!res.error) {
        setPayrollDetails(res.data || []);
        setPayrollSummary(res.summary || {});
      }
    }
  };


  useEffect(() => {
    const hrRoles = [
      // HR role
      "ForthERP_Basic_hr",
      "ForthERP_Standard_hr",
      "ForthERP_Pro_hr",
      "PharmaERP_Basic_hr",
      "PharmaERP_Standard_hr",
      "PharmaERP_Pro_hr",
      "PharmaBooks_Basic_hr",
      "PharmaBooks_Standard_hr",
      "PharmaBooks_Pro_hr",
      "ForthBooks_Basic_hr",
      "ForthBooks_Standard_hr",
      "ForthBooks_Pro_hr",
      "HRPayroll_Standard_hr",
      "HRPayroll_Pro_hr",
      "SuperERP_hr",
      'ForthCRM_Pro_hr',
      'ForthCRM_Standard_hr',
      "ForthERP_Basic_admin",
      "ForthERP_Standard_admin",
      "ForthERP_Pro_admin",
      "PharmaERP_Basic_admin",
      "PharmaERP_Standard_admin",
      "PharmaERP_Pro_admin",
      "PharmaBooks_Basic_admin",
      "PharmaBooks_Standard_admin",
      "PharmaBooks_Pro_admin",
      "ForthBooks_Basic_admin",
      "ForthBooks_Standard_admin",
      "ForthBooks_Pro_admin",
      "HRPayroll_Standard_admin",
      "HRPayroll_Pro_admin",
      "SuperERP_admin",
      "ForthCRM_Standard_admin",
      "ForthCRM_Pro_admin",
    ];

    const userRoles = authInfo.userInfo.user_role_names
      ? authInfo.userInfo.user_role_names.split(",").map((role) => role.trim())
      : [];

    const isAdminHR = userRoles.some((role) => hrRoles.includes(role));

    isAdminUserSet(isAdminHR);
  }, [authInfo]);

  const isEmployee = authInfo?.employee_id && authInfo.employee_id !== 0;


  useEffect(() => {
    const checkRouteAccess = async () => {
      try {
        const res = await checkEmployeeRouteAccessApi();
        setHasRouteAccess(res?.data?.has_route_access);
      } catch (err) {
        console.error(err);
      }
    };

    if (isEmployee && !isAdminUser) {
      checkRouteAccess();
    }
  }, [isEmployee, isAdminUser]);

  return (

    <div className="dashboard-container">

      <DashboardHeader />

      <WelcomeCard data={data} isAdminUser={isAdminUser} />

      {isAdminUser && <KpiCards data={data} onCardClick={handleKpiClick} activeKey={activeKpi} />}

      {isAdminUser && (
        <div style={{ textAlign: "right", margin: "-8px 0 12px" }}>
          <Button size="small" type="link" onClick={() => setUnmarkedAttendanceModal(true)}>
            Unmarked Attendance
          </Button>
        </div>
      )}

      <ShortcutBar isEmployee={isEmployee} isAdminUser={isAdminUser} hasRouteAccess={hasRouteAccess} />

      {!isAdminUser && isEmployee && <EmployeeProfilePanel employee={data.currentEmployee} />}

      {/* MAIN ANALYTICS ROW */}

      {isAdminUser && <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>

        <Col xs={24} lg={8}>

          <EmployeeStatusPanel employees={data.employeesFull} />

        </Col>

        <Col xs={24} lg={8}>

          <AttendanceOverviewPanel
            attendanceReport={data.todayAttendanceReport}
            pendingRequests={data.pendingRequests}
            employees={data.employees}
            rangeType={data.rangeType}
            onRangeChange={data.setRangeType}
          />

        </Col>

        <Col xs={24} lg={8}>

          <ClockInOutPanel checkins={data.todayCheckins || []} />

        </Col>

      </Row>}

      {/* ANALYTICS + NOTIFICATIONS */}

      {isAdminUser && <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>

        <Col xs={24} lg={16}>

          <DashboardCharts
            analyticsReport={data.monthlyAttendanceReport}
            trendReport={data.monthlyTrendAttendanceReport}
            employees={data.employees}
            analyticsMonth={data.analyticsMonth}
            analyticsYear={data.analyticsYear}
            setAnalyticsMonth={data.setAnalyticsMonth}
            setAnalyticsYear={data.setAnalyticsYear}
          />

        </Col>

        <Col xs={24} lg={8}>

          <NotificationPanel
            attendanceNotifications={data.attendanceNotifications}
            expiries={data.expiries}
            activities={data.activities}
            notificationsCount={data.notificationsCount}
          />

        </Col>

      </Row>}

      {/* BOTTOM ROW */}

      {/* {isAdminUser && <Row gutter={[16,16]}>

<Col xs={24} md={8}>

<ExpiryList expiries={data.expiries}/>

</Col>

<Col xs={24} md={8}>

<ActivityList activities={data.activities}/>

</Col>

<Col xs={24} md={8}>

<QuickStats data={data}/>

</Col>

</Row>} */}

      {/* upcoming renewals modal */}
      <Modal
        // title="Upcoming Renewals"
        open={activeDrawer === "renewals"}
        onCancel={() => setActiveDrawer(null)}   // ✅ correct prop
        footer={null}                            // ✅ removes OK/Cancel buttons
        width={400}
        styles={{
          body: {
            maxHeight: "60vh",                     // ✅ limit height
            overflowY: "auto",                     // ✅ enable scroll
            scrollbarWidth: "thin",
            paddingRight: 24                        // (optional) better scroll spacing
          }
        }}
      >
        <ExpiryList expiries={data.expiries} />
      </Modal>

      {/* pending approvals modal */}
      <PendingApprovalsModal
        open={pendingModal}
        onClose={() => setPendingModal(false)}
        data={data.pendingRequests?.pending || []}
        employees={data.employees}
      />

      {/* unmarked attendance modal */}
      <UnmarkedAttendanceModal
        open={unmarkedAttendanceModal}
        onClose={() => setUnmarkedAttendanceModal(false)}
        employees={data.employees}
      />

      {/* payroll status modal */}
      <PayrollDetailsModal
        open={payrollModal}
        onClose={() => setPayrollModal(false)}
        data={payrollDetails.data}
        summary={payrollDetails.summary}
        defaultTab={modalTab}
        loading={loadingPayroll}
      />

    </div>

  );

}